import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../../firebase/config';
import { FaFilter } from 'react-icons/fa';
import ProductCard from '../../components/ProductCard/ProductCard';
import styles from './Shop.module.css';

function Shop({ lang }) {
  const t = {
    en: {
      shopHeader: 'Our Products',
      products: 'Products',
      specialOffers: 'Special Offers',
      aman: '2Nice',
      affiliateBanner: "You're viewing products through an affiliate link",
      searchPlaceholder: 'Search products...',
      loading: 'Loading products...',
      filters: 'Filters',
      categories: 'Categories',
      all: 'All',
      tryAgain: 'Try Again',
      error: 'Failed to load products',
      noProducts: 'No products found.',
      amanHeading: '2Nice Products',
      amanInfo: 'These are our exclusive 2Nice products. You can browse through our collection here, but to purchase these items, please visit our physical store locations. Our staff will be happy to assist you with these special products.',
      noAman: 'No 2Nice products available right now.',
    },
    ar: {
      shopHeader: 'منتجاتنا',
      products: 'المنتجات',
      specialOffers: 'عروض خاصة',
      aman: '2Nice',
      affiliateBanner: 'أنت تشاهد المنتجات من خلال رابط تسويق بالعمولة',
      searchPlaceholder: 'ابحث عن المنتجات...',
      loading: 'جاري تحميل المنتجات...',
      filters: 'الفلاتر',
      categories: 'الفئات',
      all: 'الكل',
      tryAgain: 'حاول مرة أخرى',
      error: 'فشل تحميل المنتجات',
      noProducts: 'لا توجد منتجات.',
      amanHeading: 'منتجات 2Nice',
      amanInfo: 'هذه هي منتجات 2Nice الحصرية. يمكنك تصفح المجموعة هنا، وللشراء يرجى زيارة فروعنا. فريقنا سيساعدك بكل سرور في هذه المنتجات الخاصة.',
      noAman: 'لا توجد منتجات 2Nice متاحة حالياً.',
    },
    tr: {
      shopHeader: 'Ürünlerimiz',
      products: 'Ürünler',
      specialOffers: 'Özel Teklifler',
      aman: '2Nice',
      affiliateBanner: 'Ürünleri bir ortaklık bağlantısı üzerinden görüntülüyorsunuz',
      searchPlaceholder: 'Ürünlerde ara...',
      loading: 'Ürünler yükleniyor...',
      filters: 'Filtreler',
      categories: 'Kategoriler',
      all: 'Tümü',
      tryAgain: 'Tekrar Dene',
      error: 'Ürünler yüklenemedi',
      noProducts: 'Ürün bulunamadı.',
      amanHeading: '2Nice Ürünleri',
      amanInfo: 'Bunlar özel 2Nice ürünlerimizdir. Koleksiyonumuzu burada inceleyebilirsiniz, ancak bu ürünleri satın almak için lütfen fiziksel mağazalarımızı ziyaret edin. Ekibimiz size yardımcı olmaktan memnuniyet duyacaktır.',
      noAman: 'Şu anda 2Nice ürünü bulunmamaktadır.',
    }
  }[lang];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const affiliateId = searchParams.get('aff');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [offerProducts, setOfferProducts] = useState([]);
  const [amanProducts, setAmanProducts] = useState([]); 
  const offersRef = useRef(null);
  const amanRef = useRef(null); 
  const [activeTab, setActiveTab] = useState('products');

  // Fetch categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesSnapshot = await getDocs(collection(db, 'categories'));
        const categoriesData = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesData);
      } catch (error) {
        setError('Error fetching categories');
      }
    };

    fetchCategories();
  }, []);

  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const productsRef = collection(db, 'products');
        let q = selectedCategory && selectedCategory !== 'all' 
          ? query(productsRef, where('category', '==', selectedCategory))
          : query(productsRef);

        const querySnapshot = await getDocs(q);
        const allProducts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Separate offers, aman products, and regular products
        const offers = allProducts.filter(product => product.isOffer);
        const aman = allProducts.filter(product => product.isAman);
        const regular = allProducts.filter(product => !product.isOffer && !product.isAman);
        
        setOfferProducts(offers);
        setAmanProducts(aman);
        setProducts(regular);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedCategory]);

  const scrollToOffers = () => {
    offersRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  // Filter and sort products
  const getFilteredProducts = () => {
    try {
      if (!products.length) return [];

      let filtered = products;

      // Apply search filter
      if (searchQuery) {
        filtered = filtered.filter(product => {
          const searchLower = searchQuery.toLowerCase();
          return (
            (product.name && product.name.toLowerCase().includes(searchLower)) ||
            (product.description && product.description.toLowerCase().includes(searchLower))
          );
        });
      }

      // Sort products
      return filtered.sort((a, b) => {
        switch (sortBy) {
          case 'priceAsc':
            return (Number(a.price) || 0) - (Number(b.price) || 0);
          case 'priceDesc':
            return (Number(b.price) || 0) - (Number(a.price) || 0);
          case 'newest':
            return new Date(b.createdAt) - new Date(a.createdAt);
          default:
            return 0;
        }
      });
    } catch (err) {
      console.error('Error filtering products:', err);
      return [];
    }
  };



  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{t.error}</p>
        <button onClick={() => window.location.reload()}>{t.tryAgain}</button>
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();

  // Alphabetically sort categories before rendering
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={styles.shop}>
      <div className={styles.shopHeader}>
        <h1>{t.shopHeader}</h1>
        <div className={styles.shopNav}>
          <button
            className={`${styles.navButton} ${activeTab === 'products' ? styles.active : ''}`}
            onClick={() => setActiveTab('products')}
          >
            {t.products}
          </button>
          <button
            className={`${styles.navButton} ${activeTab === 'offers' ? styles.active : ''}`}
            onClick={() => setActiveTab('offers')}
          >
            {t.specialOffers}
          </button>
          <button
            className={`${styles.navButton} ${activeTab === 'aman' ? styles.active : ''}`}
            onClick={() => setActiveTab('aman')}
          >
            {t.aman}
          </button>
        </div>
        {affiliateId && (
          <div className={styles.affiliateBanner}>
            <p>{t.affiliateBanner}</p>
          </div>
        )}
      </div>

      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder={t.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className={styles.shopContainer}>
        <div className={styles.filters}>
          <div className={styles.filterHeader}>
            <FaFilter />
            <h2>{t.filters}</h2>
          </div>

          <div className={styles.filterSection}>
            <h3>{t.categories}</h3>
            <div className={styles.categoryList}>
              <button
                className={`${styles.categoryButton} ${selectedCategory === 'all' ? styles.active : ''}`}
                onClick={() => handleCategorySelect('all')}
              >
                {t.all}
              </button>
              {sortedCategories.map(category => (
                <button
                  key={category.id}
                  className={`${styles.categoryButton} ${selectedCategory === category.id ? styles.active : ''}`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  {t.categoryPrefix} {category.name}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.filterSection}>
            <h3>Sort By</h3>
            <select
              className={styles.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="priceAsc">Price: Low to High</option>
              <option value="priceDesc">Price: High to Low</option>
            </select>
          </div>
        </div>

        <div className={styles.productsContainer}>
          {activeTab === 'products' && (
            <div className={styles.productsGrid}>
              {filteredProducts.length === 0 ? (
                <div className={styles.noProducts}>
                  <p>{t.noProducts}</p>
                  {searchQuery && (
                    <button 
                      className={styles.clearSearch}
                      onClick={() => setSearchQuery('')}
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              ) : (
                filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    lang={lang}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'offers' && (
            <div className={styles.offersSection} ref={offersRef}>
              <h2>Special Offers</h2>
              {offerProducts.length === 0 ? (
                <p className={styles.noOffers}>No special offers available right now.</p>
              ) : (
                <div className={styles.productsGrid}>
                  {offerProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      lang={lang}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'aman' && (
            <div className={styles.amanSection} ref={amanRef}>
              <h2>{t.amanHeading}</h2>
              <div className={styles.amanInfoBox}>
                <p>{t.amanInfo}</p>
              </div>
              {amanProducts.length === 0 ? (
                <p className={styles.noAman}>{t.noAman}</p>
              ) : (
                <div className={styles.productsGrid}>
                  {amanProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      lang={lang}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Shop;